/**
 * TruBeauté — first-party analytics client.
 *
 * Routes explicit events to PostHog when configured, or the legacy backend.
 * Stealth-aware: if the user has Stealth Mode on, we record nothing.
 *
 * Usage:
 *   import { track } from "../lib/analytics";
 *   track("onboarding_step_completed", { step: "lip_finish" });
 *
 * Design notes:
 *  - Session id lives in sessionStorage, so it dies with the tab. We never
 *    fingerprint across tabs or sessions.
 *  - Failures are swallowed. Analytics MUST NEVER break the UX.
 *  - Legacy mode posts each event; PostHog mode batches through posthogAnalytics.
 */
import axios from "axios";
import { safeLocal, safeSession } from "./safeStorage";
import { STEALTH_KEY, ANALYTICS_SESSION_KEY } from "./storageKeys";
import { getStoredUtm } from "./utm";
import { usesPostHog, capturePostHog } from "./posthogAnalytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const _uuid = () =>
  // RFC4122-ish v4 UUID without crypto dep (good enough for session ids)
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );

const _getSessionId = () => {
  let id = safeSession.getItem(ANALYTICS_SESSION_KEY);
  if (!id) {
    id = _uuid();
    safeSession.setItem(ANALYTICS_SESSION_KEY, id);
  }
  return id;
};

const _isStealth = () => safeLocal.getItem(STEALTH_KEY) === "1";

// `/demo` is a deliberately non-persistent product walkthrough. Do not mint
// an analytics session id, read attribution storage, or send an event while
// any demo sub-route is active.
const _isEphemeralDemoRoute = () =>
  typeof window !== "undefined" &&
  (window.location.pathname === "/demo" ||
    window.location.pathname.startsWith("/demo/"));

/**
 * Fire an analytics event. Cheap, async, never throws.
 */
export const track = (event, props = undefined) => {
  if (usesPostHog()) {
    capturePostHog(event, props, "legacy_client");
    return;
  }
  if (!event || typeof event !== "string") return;
  if (_isEphemeralDemoRoute()) return;
  if (_isStealth()) return;                      // hard kill in stealth

  // Public Demo Mode — auto-merge `demo_mode:true` and swap the
  // session id to the demo-specific one so demo activity is
  // filterable from prod metrics without one-off plumbing.
  const inDemoMode =
    typeof window !== "undefined" && !!window.__trubeaute_demo_mode;
  const demoSessionId =
    inDemoMode && typeof window !== "undefined"
      ? window.__trubeaute_demo_session_id || null
      : null;

  // Auto-merge any captured UTM / click-id params onto every event.
  // utm_* + gclid/fbclid/ttclid/msclkid travel with every event in
  // a session so /admin/analytics can attribute landing → signup →
  // purchase to the originating campaign without per-event plumbing.
  const utm = getStoredUtm();
  const baseProps = utm ? { ...utm, ...(props || {}) } : props;
  const mergedProps = inDemoMode
    ? { ...(baseProps || {}), demo_mode: true }
    : baseProps;

  const payload = {
    event,
    props: mergedProps || undefined,
    page_path:
      typeof window !== "undefined" ? window.location.pathname : undefined,
    session_id: demoSessionId || _getSessionId(),
  };

  try {
    // withCredentials so the auth cookie travels with the event — lets the
    // backend tag is_guest / is_pro for free.
    axios
      .post(`${API}/events`, payload, {
        withCredentials: true,
        timeout: 6000,
      })
      .catch((err) => {
        // Swallow — analytics must never bubble errors to the user.
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[analytics] track POST failed:", err?.message || err);
        }
      });
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[analytics] track threw:", err?.message || err);
    }
  }
};

/**
 * Fire a Google Analytics 4 `page_view` event for the current React
 * route. Counterpart to `track()` but for GA4 (third-party) instead
 * of our first-party `/api/events` pipeline.
 *
 * Same stealth-mode kill-switch applies: if the user has Stealth
 * Mode on, this is a no-op. (And as a defense-in-depth, the
 * inline bootstrap in index.html refuses to load gtag.js at all
 * for stealth users — so even if React forgets to gate, the
 * function call will be a no-op because window.gtag won't exist.)
 *
 * Called from AppRouter on every React Router location change.
 */
export const gaPageView = (pathname, search = "") => {
  if (pathname === "/demo" || pathname?.startsWith("/demo/")) return;
  if (_isStealth()) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (window.__trubeaute_ga_disabled) return;
  const path = `${pathname || "/"}${search || ""}`;
  try {
    window.gtag("event", "page_view", {
      page_path: path,
      page_title: typeof document !== "undefined" ? document.title : undefined,
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
    });
  } catch (e) {
    // Never let analytics break navigation.
  }
};

export const fetchAnalyticsSummary = async (days = 30) => {
  const { data } = await axios.get(
    `${API}/admin/analytics/summary?days=${days}`,
    { withCredentials: true },
  );
  return data;
};


// Conversion funnel for the new consultation flow (Jun 6 2026
// launch-readiness sprint). Backed by GET /api/admin/analytics/funnel
// which now returns both the original 8-stage `funnel` AND a new
// `consultation_funnel` (Landing → Consultation Started → Selfie →
// Routine → Signup → Subscription).
export const fetchAnalyticsFunnel = async (days = 30) => {
  const { data } = await axios.get(
    `${API}/admin/analytics/funnel?days=${days}`,
    { withCredentials: true },
  );
  return data;
};


// Admin engagement (investor-grade traction metrics) — aggregate-only,
// stealth-safe. Returns DAU/WAU/MAU, free-tier engagement, free→Pro
// funnel, weekly cohort retention, and stealth-mode adoption.
export const fetchAdminEngagement = async () => {
  const { data } = await axios.get(
    `${API}/admin/engagement`,
    { withCredentials: true },
  );
  return data;
};

// Admin customer detail — decrypts email/name + payment history.
// Audit-logged server-side; admin-only.
export const fetchAdminCustomer = async (userId) => {
  const { data } = await axios.get(
    `${API}/admin/customers/${encodeURIComponent(userId)}`,
    { withCredentials: true },
  );
  return data;
};

// Admin: send a personalized founder-welcome email to a paying
// customer. Idempotent unless `force=true`. Resend-backed.
export const sendAdminCustomerWelcome = async (userId, force = false) => {
  const { data } = await axios.post(
    `${API}/admin/customers/${encodeURIComponent(userId)}/send-welcome-email`,
    { force },
    { withCredentials: true },
  );
  return data;
};

// Admin stealth cohort — aggregate counts only (no user_ids). Safe to
// surface alongside investor metrics; the founder uses this to see
// how many real humans rely on the privacy promise.
export const fetchAdminStealthCohort = async () => {
  const { data } = await axios.get(
    `${API}/admin/stealth/cohort`,
    { withCredentials: true },
  );
  return data;
};

// Admin stealth users — decrypted-email list of every real user with
// at least one stealth routine. Server-side audit-logged on every call.
export const fetchAdminStealthUsers = async (limit = 200) => {
  const { data } = await axios.get(
    `${API}/admin/stealth/users?limit=${encodeURIComponent(limit)}`,
    { withCredentials: true },
  );
  return data;
};
